import { ConditionGroup, ReflowRow } from 'editor/src/store/design/types';

import getConditionChildrenKey from '../reducer/getConditionChildrenKey';

/**
 * A condition's options are mutually-exclusive alternatives that occupy the
 * same slot — each option links its own text element, only one of which is
 * visible at a time. For reflow to behave when the buyer switches options,
 * every option's text must belong to the same reflow row (the engine already
 * treats the inactive ones as condition-hidden and shifts them together).
 *
 * Given an element uuid, returns the text-element uuids of every option of the
 * condition that owns it (including the element itself). Returns [] when the
 * element isn't linked to any condition option. Only `text` children are
 * returned — reflow only operates on text elements.
 */
export function getConditionSiblingElementUuids(conditionGroup: ConditionGroup, elementUuid: number): number[] {
  const ownerId = Object.keys(conditionGroup.conditions).find((conditionId) =>
    conditionGroup.conditions[conditionId].options.some((option) => {
      const children = conditionGroup.children[getConditionChildrenKey(conditionId, option.id)];
      return children?.some((child) => child.type !== 'condition' && child.id === elementUuid);
    }),
  );

  if (!ownerId) {
    return [];
  }

  const siblings: number[] = [];
  conditionGroup.conditions[ownerId].options.forEach((option) => {
    const children = conditionGroup.children[getConditionChildrenKey(ownerId, option.id)];
    children?.forEach((child) => {
      if (child.type === 'text' && !siblings.includes(child.id)) {
        siblings.push(child.id);
      }
    });
  });
  return siblings;
}

/**
 * Expands each reflow row so that, for any member that is a condition option's
 * text, all sibling option texts of that condition join the same row. Used when
 * a reflow group is created from a canvas selection — the selection only
 * contains the active (visible) option, so without this the other options would
 * never reflow. Pure; preserves the one-element-one-row invariant by assigning
 * each sibling to the first row that pulls it in.
 */
export function expandReflowRowsWithConditionSiblings(rows: ReflowRow[], conditionGroup: ConditionGroup): ReflowRow[] {
  const assigned = new Set<number>();
  return rows.map((row) => {
    const elementUuids = [...row.elementUuids];
    elementUuids.forEach((uuid) => assigned.add(uuid));
    row.elementUuids.forEach((uuid) => {
      getConditionSiblingElementUuids(conditionGroup, uuid).forEach((sibling) => {
        if (!assigned.has(sibling)) {
          assigned.add(sibling);
          elementUuids.push(sibling);
        }
      });
    });
    return { ...row, elementUuids };
  });
}
